import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Checkbox, IconButton, Stack, Typography } from '@mui/material';
import LoadingOverlayButton from '../button/loading-overlay-button.component';
import DialogComponent from '../dialog/dialog.component';
import i18n from '../../i18n';
import { ReactComponent as CloseIcon } from '../../static/images/svg/mingcute_close-fill.svg';
import { ReactComponent as SearchIcon } from '../../static/images/svg/ic_round-search.svg';
import { ReactComponent as StoreIcon } from '../../static/images/svg/bxs_store.svg';
import { ReactComponent as MenuBookIcon } from '../../static/images/svg/material-symbols_menu-book-2-rounded.svg';
import { ReactComponent as CheckboxBlankIcon } from '../../static/images/svg/material-symbols_check-box-outline-blank.svg';
import './share-menu-dialog.style.scss';

/** 门店选项（string_id 作为唯一值） */
export type ShareMenuStoreOption = {
    /** 餐厅 string_id */
    value: string;
    /** 展示名称 */
    label: string;
};

export type ShareMenuDialogProps = {
    /** 是否打开 */
    open: boolean;
    /** 关闭回调 */
    onClose: () => void;
    /** 当前菜单名称（副标题）；可含 ` - DD/MM/YYYY` 更新时间后缀，与菜单库列表卡片日期格式一致 */
    menuName: string;
    /** 可选门店列表 */
    storeOptions: ShareMenuStoreOption[];
    /**
     * 当前菜单所在/上下文餐厅的 string_id。
     * 传入时从列表中剔除该门店（本地分享：只能同步到别的门店）。
     * 云端分享不传：由调用方把「当前门店-XXXX」置顶放入 `storeOptions`。
     */
    currentContextStoreId?: string;
    /**
     * 同步至所选门店（全宽按钮与双列右侧按钮共用）
     * @param selectedStoreIds 选中的 string_id 列表
     * @param allStoreOptions 全部门店选项，用于成功文案等解析名称
     */
    onSyncToSelectedStores?: (selectedStoreIds: string[], allStoreOptions: ShareMenuStoreOption[]) => void;
    /** 同步至菜单商店 */
    onSyncToMenuStore?: () => void;
    /** 正在拉取「商店目标」列表（主按钮 loading，勿关弹窗） */
    menuStoreEntryLoading?: boolean;
    /** 正在提交同步至所选门店 */
    selectedStoresSubmitting?: boolean;
    /**
     * 是否展示「同步至菜单商店」及底部双按钮行。
     * - `true`：仅渲染底部一行（「同步至菜单商店」+「同步至所选门店」），**不渲染**上方全宽「同步至所选门店」。
     * - `false`：仅渲染上方全宽「同步至所选门店」，**不渲染**底部一行（无菜单商店入口）。
     * @default true
     */
    showMenuStoreSync?: boolean;
    /** 根节点 className */
    className?: string;
};

const checkboxIconSx = { width: 40, height: 40 };

/** 未选：描边使用 currentColor，由外层传入主题色 */
const checkboxUncheckedSx = { ...checkboxIconSx, color: 'var(--fill-primary-300)' };

/**
 * 已选勾选框：底填充为图标/按钮主题色 300，勾为浅色（与 Figma material-symbols check-box-rounded 一致）
 */
function ShareMenuCheckboxCheckedGraphic() {
    return (
        <svg width={40} height={40} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <rect x="5" y="5" width="30" height="30" rx="4" fill="var(--fill-primary-300)" />
            <path
                fill="var(--fill-neutral-100)"
                d="M17.6667 22.3333L14.0833 18.75C13.7778 18.4444 13.3889 18.2917 12.9167 18.2917C12.4444 18.2917 12.0556 18.4444 11.75 18.75C11.4444 19.0556 11.2917 19.4444 11.2917 19.9167C11.2917 20.3889 11.4444 20.7778 11.75 21.0833L16.5 25.8333C16.8333 26.1667 17.2222 26.3333 17.6667 26.3333C18.1111 26.3333 18.5 26.1667 18.8333 25.8333L28.25 16.4167C28.5556 16.1111 28.7083 15.7222 28.7083 15.25C28.7083 14.7778 28.5556 14.3889 28.25 14.0833C27.9444 13.7778 27.5556 13.625 27.0833 13.625C26.6111 13.625 26.2222 13.7778 25.9167 14.0833L17.6667 22.3333Z"
            />
        </svg>
    );
}

/**
 * 分享菜单弹窗（Figma 3187:52409 / 3187:52411）：标题、查找门店、多选列表、同步操作按钮。
 * 壳层使用公共 `DialogComponent`，并通过 `sx` / `PaperProps` 覆盖为白底以与稿一致。
 */
export function ShareMenuDialog(props: ShareMenuDialogProps) {
    const {
        open,
        onClose,
        menuName,
        storeOptions,
        currentContextStoreId,
        onSyncToSelectedStores,
        onSyncToMenuStore,
        menuStoreEntryLoading = false,
        selectedStoresSubmitting = false,
        showMenuStoreSync = true,
        className,
    } = props;

    /** 主弹窗内异步进行中：禁止关闭与重复点击 */
    const isShareDialogBusy = menuStoreEntryLoading || selectedStoresSubmitting;

    /** 查找门店关键字 */
    const [searchKeyword, setSearchKeyword] = useState('');
    /** 已选门店 string_id */
    const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

    useEffect(() => {
        if (!open) {
            return;
        }
        setSearchKeyword('');
        setSelectedIds(new Set());
    }, [open]);

    /** 排除当前上下文餐厅后的可选门店 */
    const shareableStores = useMemo(() => {
        const id = currentContextStoreId?.trim();
        if (!id) {
            return storeOptions;
        }
        return storeOptions.filter((s) => s.value !== id);
    }, [storeOptions, currentContextStoreId]);

    /** 按关键字过滤后的门店（不含当前餐厅） */
    const filteredStores = useMemo(() => {
        const q = searchKeyword.trim().toLowerCase();
        if (!q) {
            return shareableStores;
        }
        return shareableStores.filter((s) => s.label.toLowerCase().includes(q));
    }, [shareableStores, searchKeyword]);

    const allFilteredSelected =
        filteredStores.length > 0 && filteredStores.every((s) => selectedIds.has(s.value));

    const toggleOne = useCallback((id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    const handleSelectAllFiltered = useCallback(() => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (allFilteredSelected) {
                filteredStores.forEach((s) => next.delete(s.value));
            } else {
                filteredStores.forEach((s) => next.add(s.value));
            }
            return next;
        });
    }, [allFilteredSelected, filteredStores]);

    const handleSyncSelected = useCallback(() => {
        const ids = Array.from(selectedIds);
        onSyncToSelectedStores?.(ids, storeOptions);
    }, [onSyncToSelectedStores, selectedIds, storeOptions]);

    const subtitleText = menuName.trim() || '—';

    /** 请求中忽略遮罩关闭，避免误关后状态不一致 */
    const handleDialogClose = useCallback(
        (_event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
            if (isShareDialogBusy && reason === 'backdropClick') {
                return;
            }
            if (isShareDialogBusy && reason === 'escapeKeyDown') {
                return;
            }
            onClose();
        },
        [isShareDialogBusy, onClose],
    );

    return (
        <DialogComponent
            className={['share-menu-dialog', className].filter(Boolean).join(' ')}
            open={open}
            onClose={handleDialogClose}
            maxWidth={false}
            fullWidth
            aria-labelledby="share-menu-dialog-title"
            PaperProps={{
                className: 'share-menu-dialog__paper',
                elevation: 0,
                sx: {
                    maxWidth: '640px',
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'var(--fill-neutral-100)',
                    backgroundColor: 'var(--fill-neutral-100)',
                    border: '2px solid var(--fill-primary-300)',
                    boxShadow: '0 0 4px 0 var(--shadow-25)',
                },
            }}
            sx={[
                {
                    '& .MuiBackdrop-root': {
                        backgroundColor: 'var(--overlay-800-a25)',
                    },
                    '& .MuiDialog-paper': {
                        marginTop: '-32px',
                    },
                    '& .MuiDialog-container > .MuiPaper-root': {
                        background: 'var(--fill-neutral-100)',
                        backgroundColor: 'var(--fill-neutral-100)',
                        border: '2px solid var(--fill-primary-300)',
                        boxShadow: '0 0 4px 0 var(--shadow-25)',
                    },
                    '& .MuiDialogTitle-root': {
                        backgroundColor: 'var(--fill-neutral-100)',
                        paddingBottom: '8px',
                        paddingLeft: '24px',
                        paddingRight: '24px',
                        width: '100%',
                        boxSizing: 'border-box',
                    },
                    '& .MuiDialogContent-root': {
                        paddingTop: '24px !important',
                    },
                    '& .dialog-footer': {
                        display: 'none',
                    },
                },
            ]}
            dialogContentStyle={{
                paddingTop: '0px',
                paddingLeft: '24px',
                paddingRight: '24px',
                paddingBottom: '24px',
            }}
            defaultHeaderProps={{
                titleAlign: 'left',
                isHiddenCloseIcon: true,
                onClose: () => onClose(),
                titleContent: (
                    <Box className="share-menu-dialog__header" sx={{ width: '100%', boxSizing: 'border-box' }}>
                        <Box className="share-menu-dialog__title-block">
                            <Typography
                                id="share-menu-dialog-title"
                                component="h2"
                                className="share-menu-dialog__title"
                            >
                                {i18n.t('menu_library_detail_share_menu')}
                            </Typography>
                            <Typography component="p" className="share-menu-dialog__subtitle">
                                {subtitleText}
                            </Typography>
                        </Box>
                        <IconButton
                            type="button"
                            size="small"
                            onClick={onClose}
                            disabled={isShareDialogBusy}
                            aria-label={i18n.t('extra_cost_text_close')}
                            className="share-menu-dialog__close"
                        >
                            <CloseIcon width={24} height={24} aria-hidden />
                        </IconButton>
                    </Box>
                ),
            }}
        >
            <Stack spacing={3}>
                <Box className="share-menu-dialog__search-row">
                    <Box className="share-menu-dialog__search-pill" component="label">
                        <SearchIcon className="share-menu-dialog__search-icon" aria-hidden />
                        <input
                            type="search"
                            className="share-menu-dialog__search-input"
                            placeholder={i18n.t('menu_library_share_dialog_search_placeholder')}
                            value={searchKeyword}
                            onChange={(e) => setSearchKeyword(e.currentTarget.value)}
                            disabled={isShareDialogBusy}
                            aria-label={i18n.t('menu_library_share_dialog_search_placeholder')}
                        />
                    </Box>
                    <Button
                        type="button"
                        variant="text"
                        className="share-menu-dialog__select-all"
                        onClick={handleSelectAllFiltered}
                        disabled={filteredStores.length === 0 || isShareDialogBusy}
                    >
                        {i18n.t('menu_library_share_dialog_select_all')}
                    </Button>
                </Box>

                <Box className="share-menu-dialog__list" role="list">
                    {filteredStores.length === 0 ? (
                        <Box className="share-menu-dialog__list-empty">
                            <Typography variant="body2" color="text.secondary">
                                {i18n.t('general_text_no_or_more_data')}
                            </Typography>
                        </Box>
                    ) : (
                        filteredStores.map((store, index) => (
                            <React.Fragment key={store.value}>
                                {index > 0 ? <hr className="share-menu-dialog__divider" /> : null}
                                <Box className="share-menu-dialog__row" role="listitem">
                                    <Checkbox
                                        checked={selectedIds.has(store.value)}
                                        onChange={() => toggleOne(store.value)}
                                        disabled={isShareDialogBusy}
                                        icon={<CheckboxBlankIcon style={checkboxUncheckedSx} />}
                                        checkedIcon={<ShareMenuCheckboxCheckedGraphic />}
                                        inputProps={{
                                            'aria-label': store.label,
                                        }}
                                        sx={{
                                            p: 0.5,
                                            color: 'var(--fill-primary-300)',
                                            '&.Mui-checked': {
                                                color: 'var(--fill-primary-300)',
                                            },
                                        }}
                                    />
                                    <Typography component="span" className="share-menu-dialog__row-label" title={store.label}>
                                        {store.label}
                                    </Typography>
                                </Box>
                            </React.Fragment>
                        ))
                    )}
                </Box>

                {!showMenuStoreSync ? (
                    <LoadingOverlayButton
                        type="button"
                        variant="contained"
                        disableElevation
                        fullWidth
                        className="share-menu-dialog__action"
                        disabled={isShareDialogBusy}
                        loading={selectedStoresSubmitting}
                        loadingIndicatorSize={22}
                        onClick={handleSyncSelected}
                        startIcon={<StoreIcon className="share-menu-dialog__action-icon" aria-hidden />}
                    >
                        {i18n.t('menu_library_share_dialog_sync_selected')}
                    </LoadingOverlayButton>
                ) : null}

                {showMenuStoreSync ? (
                    <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                        <LoadingOverlayButton
                            type="button"
                            variant="contained"
                            disableElevation
                            className="share-menu-dialog__action"
                            sx={{ flex: 1 }}
                            disabled={isShareDialogBusy}
                            loading={menuStoreEntryLoading}
                            loadingIndicatorSize={22}
                            onClick={() => onSyncToMenuStore?.()}
                            startIcon={<MenuBookIcon className="share-menu-dialog__action-icon" aria-hidden />}
                        >
                            {i18n.t('menu_library_share_dialog_sync_menu_store')}
                        </LoadingOverlayButton>
                        <LoadingOverlayButton
                            type="button"
                            variant="contained"
                            disableElevation
                            className="share-menu-dialog__action"
                            sx={{ flex: 1 }}
                            disabled={isShareDialogBusy}
                            loading={selectedStoresSubmitting}
                            loadingIndicatorSize={22}
                            onClick={handleSyncSelected}
                            startIcon={<StoreIcon className="share-menu-dialog__action-icon" aria-hidden />}
                        >
                            {i18n.t('menu_library_share_dialog_sync_selected')}
                        </LoadingOverlayButton>
                    </Stack>
                ) : null}
            </Stack>
        </DialogComponent>
    );
}

export default ShareMenuDialog;
